import { useState, useEffect } from 'react';
import {
  doc, getDoc, updateDoc, collection, getDocs, query, where,
  arrayRemove, arrayUnion, deleteDoc, setDoc, runTransaction
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  updateEmail,
  updatePassword
} from 'firebase/auth';
import { auth, db, functions } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';

function AccountSettings() {
  const [userData, setUserData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [membersData, setMembersData] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newAdminId, setNewAdminId] = useState('');
  const [modal, setModal] = useState({ open: false, type: '', member: null });
  const [modalEmail, setModalEmail] = useState('');
  const [modalName, setModalName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState(''); // New state for initial password
  const [confirmPassword, setConfirmPassword] = useState(''); // Confirm password state
  
  // Super Admin Configuration - Add your emails here
  const SUPER_ADMINS = [
    'danielc@zentekconsultants.net',  // Daniel
    'jimc@zentekconsultants.net',     // Jim
    'davidm@zentekconsultants.net',   // David
  ];
  
  // Check if current user is a super admin
  const isSuperAdmin = userData?.email && SUPER_ADMINS.includes(userData.email);
  
  // DEBUG: Log super admin check
  console.log('🔍 Super Admin Debug:', {
    userEmail: userData?.email,
    superAdminEmails: SUPER_ADMINS,
    isSuperAdmin: isSuperAdmin
  });
  
  // Super admin states
  const [showSuperAdminPanel, setShowSuperAdminPanel] = useState(false);
  const [targetCompanyId, setTargetCompanyId] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [manualLicenseCount, setManualLicenseCount] = useState(1);
  const [manualNotes, setManualNotes] = useState('');
  const [licenseToDelete, setLicenseToDelete] = useState('');
  
  // Company creation states
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [initialLicenseCount, setInitialLicenseCount] = useState(1);
  
  // Add state for purchase form
  const [purchaseCount, setPurchaseCount] = useState(1);
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [purchaseStatus, setPurchaseStatus] = useState('');
  const [paypalSuccess, setPaypalSuccess] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState(null);
  
  // Grace period extension states
  const [extensionCompanyId, setExtensionCompanyId] = useState('');
  const [extensionDays, setExtensionDays] = useState(7);
  const [extensionReason, setExtensionReason] = useState('');

  // Check for PayPal success on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paypalStatus = urlParams.get('paypal');
    const success = urlParams.get('success');
    const subscriptionId = urlParams.get('subscription_id');
    
    if (paypalStatus === 'success' || (success === 'true' && subscriptionId)) {
      setPaypalSuccess('PayPal payment completed successfully! Your licenses are being processed...');
      
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Refresh data immediately and then again after a short delay to catch webhook processing
      fetchData();
      setTimeout(async () => {
        const oldLicenseCount = companyData ? companyData.licenses.length : 0;
        await fetchData();
        // The fetchData will update companyData, but we need to access it in the next render cycle
        setTimeout(() => {
          const newLicenseCount = companyData ? companyData.licenses.length : 0;
          const addedLicenses = newLicenseCount - oldLicenseCount;
          if (addedLicenses > 0) {
            setPaypalSuccess(`PayPal payment completed successfully! ${addedLicenses} licenses have been added to your account.`);
          } else {
            setPaypalSuccess('PayPal payment completed successfully! Your licenses have been processed.');
          }
        }, 500);
      }, 3000); // Wait 3 seconds for webhook to process
    } else if (paypalStatus === 'cancelled') {
      setPaypalSuccess('PayPal payment was cancelled.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Move fetchData outside useEffect so it is available everywhere
  const fetchData = async () => {
    setLoading(true);
    setError('');
    if (!auth.currentUser) {
      setError('Not authenticated. Please log in.');
      setLoading(false);
      navigate('/login');
      return;
    }
    try {
      const currentUser = auth.currentUser;
      console.log('[DEBUG] Auth user:', currentUser ? { uid: currentUser.uid, email: currentUser.email } : null);

      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      console.log('[DEBUG] User doc exists:', userDoc.exists());
      if (userDoc.exists()) {
        console.log('[DEBUG] User doc data:', userDoc.data());
      }

      if (!userDoc.exists()) {
        setError('User document not found for UID: ' + currentUser.uid);
        setLoading(false);
        return;
      }
      const userData = userDoc.data();
      setUserData(userData);

      if (!userData.companyId) {
        setError('No companyId found for user.');
        setLoading(false);
        return;
      }

      const companyDocRef = doc(db, 'companies', userData.companyId);
      const companyDoc = await getDoc(companyDocRef);
      console.log('[DEBUG] Company doc exists:', companyDoc.exists());
      if (companyDoc.exists()) {
        console.log('[DEBUG] Company doc data:', companyDoc.data());
      }

      if (!companyDoc.exists()) {
        setError('Company document not found for companyId: ' + userData.companyId);
        setLoading(false);
        return;
      }
      const companyData = companyDoc.data();
      setCompanyData({ ...companyData, id: userData.companyId });
      const membersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', userData.companyId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      const membersData = membersSnapshot.docs.map(memberDoc => {
        const memberData = memberDoc.data();
        return { uid: memberDoc.id, name: memberData.name || 'Unknown', email: memberData.email };
      });
      setMembersData(membersData);
      console.log('[AccountSettings] Members loaded:', membersData);
    } catch (err) {
      console.error('[AccountSettings] Error fetching data:', err);
      setError('Error fetching data: ' + (err && err.message ? err.message : JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigate]);

  // Removed problematic emulator connection code - using production functions as configured in firebase.js

  // After fetchData, log the initial licenses
  useEffect(() => {
    if (companyData && companyData.licenses) {
      console.log('Initial licenses:', companyData.licenses);
    }
  }, [companyData]);



  const generateInviteToken = () => {
    return Math.random().toString(36).substr(2, 12) + Date.now();
  };

  const handleAssignLicense = async (e) => {
    e.preventDefault();
    if (!companyData || companyData.adminUserId !== auth.currentUser.uid || !selectedLicense || !newEmail || !newName) return;
    console.log('Assign License (invite) button clicked');
    const availableLicense = companyData.licenses.find(license => license.id === selectedLicense.id && !license.assigned);
    if (!availableLicense) {
      alert('No available license selected or license is already assigned.');
      return;
    }
    try {
      // Generate invite token
      const inviteToken = Math.random().toString(36).substr(2, 12) + Date.now();
      // Update the license with invite info and set assigned: true
      await updateDoc(doc(db, 'companies', companyData.id), {
        licenses: companyData.licenses.map(license =>
          license.id === selectedLicense.id
            ? { ...license, email: newEmail, name: newName, assigned: true, status: 'invited', inviteToken }
            : license
        )
      });
      await fetchData();
      console.log('Licenses after assignment:', companyData.licenses);
      setNewEmail('');
      setNewName('');
      setSelectedLicense(null);
      // Show invite link to admin
      const inviteUrl = `${window.location.origin}/invite?token=${inviteToken}&companyId=${companyData.id}&licenseId=${selectedLicense.id}`;
      window.prompt('Copy and send this invite link to the user:', inviteUrl);
    } catch (err) {
      console.error('Error assigning license (invite):', err.message);
      alert('Failed to generate invite link: ' + err.message);
    }
  };

  // Modal handlers
  const openModal = (type, member) => {
    setModal({ open: true, type, member });
    setModalEmail(member ? member.email : '');
    setModalName(member ? member.name : '');
  };
  const closeModal = () => {
    setModal({ open: false, type: '', member: null });
    setModalEmail('');
    setModalName('');
  };

  const handleEditUser = async () => {
    const { member } = modal;
    if (!companyData || companyData.adminUserId !== auth.currentUser.uid || !member) return;
    try {
      await updateDoc(doc(db, 'users', member.uid), { email: modalEmail, name: modalName });
      const license = companyData.licenses.find(l => l.email === member.email || l.name === member.name);
      if (license) {
        await updateDoc(doc(db, 'companies', companyData.id), {
          licenses: companyData.licenses.map(l =>
            l.id === license.id ? { ...l, email: modalEmail, name: modalName, assigned: true } : l
          )
        });
      }
      setMembersData(membersData.map(m =>
        m.uid === member.uid ? { ...m, email: modalEmail, name: modalName } : m
      ));
      closeModal();
      await fetchData();
    } catch (err) {
      console.error('[AccountSettings] Failed to edit user:', err);
      alert('Failed to edit user: ' + (err && err.message ? err.message : JSON.stringify(err)));
    }
  };

  const handleReassignLicense = async () => {
    const { member } = modal;
    if (!companyData || companyData.adminUserId !== auth.currentUser.uid || !member) return;
    try {
      const license = companyData.licenses.find(l => l.email === member.email || l.name === member.name);
      await deleteDoc(doc(db, 'users', member.uid));
      const credential = await createUserWithEmailAndPassword(auth, modalEmail, generatePassword());
      const newUserId = credential.user.uid;
      await setDoc(doc(db, 'users', newUserId), {
        email: modalEmail,
        name: modalName,
        companyId: companyData.id
      });
      await updateDoc(doc(db, 'companies', companyData.id), {
        members: arrayUnion(newUserId),
        licenses: companyData.licenses.map(l =>
          license && l.id === license.id ? { ...l, email: modalEmail, name: modalName, assigned: true } : l
        )
      });
      setMembersData(membersData.map(m =>
        m.uid === member.uid ? { uid: newUserId, email: modalEmail, name: modalName } : m
      ).filter(m => m.uid !== member.uid));
      if (member.uid === companyData.adminUserId) {
        await updateDoc(doc(db, 'companies', companyData.id), { adminUserId: newUserId });
      }
      closeModal();
      await fetchData();
    } catch (err) {
      console.error('[AccountSettings] Failed to reassign license:', err);
      alert('Failed to reassign license: ' + (err && err.message ? err.message : JSON.stringify(err)));
    }
  };

  const handleUnassignUser = async () => {
    const { member } = modal;
    if (!companyData || companyData.adminUserId !== auth.currentUser.uid || !member) return;
    let firestoreError = null;
    let authDeleteError = null;
    try {
      // Always unassign the license
      const license = companyData.licenses.find(l => l.email === member.email || l.name === member.name);
      await runTransaction(db, async (transaction) => {
        const companyRef = doc(db, 'companies', companyData.id);
        transaction.update(companyRef, {
          members: member.uid ? arrayRemove(member.uid) : companyData.members,
          licenses: companyData.licenses.map(l =>
            license && l.id === license.id ? { 
              ...l, 
              email: null, 
              name: null, 
              assigned: false, 
              inviteToken: null,
              // Preserve cancelled/suspended status, only clear 'invited' status
              status: l.status === 'invited' ? '' : l.status
            } : l
          )
        });
        // Only delete user doc if uid exists
        if (member.uid) {
          const userRef = doc(db, 'users', member.uid);
          transaction.delete(userRef);
        }
      });
      setMembersData(membersData.filter(m => m.uid !== member.uid));
      closeModal();
    } catch (err) {
      firestoreError = err.message || 'Failed to unassign user from Firestore';
      console.error('[AccountSettings] Failed to unassign user (Firestore):', firestoreError);
      alert('Failed to unassign user (Firestore): ' + firestoreError);
    }
    // Only call deleteUser if uid exists
    if (member.uid) {
      try {
        console.log('[Frontend] Calling deleteUser with:', { uid: member.uid, companyId: companyData.id });
        console.log('[Frontend] Current user:', auth.currentUser?.uid, auth.currentUser?.email);
        console.log('[Frontend] Company admin:', companyData.adminUserId);
        // Call as HTTP function instead of callable due to SDK issues
        const token = await auth.currentUser.getIdToken();
        const response = await fetch('https://us-central1-zenbidtracker.cloudfunctions.net/deleteUserHTTP', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ uid: member.uid, companyId: companyData.id })
        });
        
        const result = await response.json();
        if (result.error) {
          authDeleteError = result.error;
          console.error('Delete user error:', authDeleteError);
          
          // If it's just "user not found", that's actually fine - they might have been invited but never signed up
          if (authDeleteError.includes('User not found')) {
            console.log('User was not found in Firebase Auth - likely was invited but never signed up. This is normal.');
            authDeleteError = null; // Don't show this as an error to the user
          }
        }
      } catch (err) {
        authDeleteError = err.message || 'Failed to delete user from Firebase Authentication';
        console.error('Delete user error:', authDeleteError);
        
        // Handle common cases gracefully
        if (err.message && err.message.includes('User not found')) {
          console.log('User was not found in Firebase Auth - likely was invited but never signed up. This is normal.');
          authDeleteError = null; // Don't show this as an error to the user
        }
      }
    }
    // Always refresh data
    await fetchData();
    if (authDeleteError && !firestoreError) {
      console.warn('User removed from Firestore, but failed to delete from Firebase Auth:', authDeleteError);
      // Only show critical errors to user, not common "user not found" cases
      if (!authDeleteError.includes('User not found') && !authDeleteError.includes('not found')) {
        alert('User removed successfully, but there was an issue with authentication cleanup: ' + authDeleteError);
      }
    }
  };

  const handleSetNewAdmin = async () => {
    if (!companyData || companyData.adminUserId !== auth.currentUser.uid || !newAdminId) return;
    try {
      await updateDoc(doc(db, 'companies', companyData.id), { adminUserId: newAdminId });
      fetchData();
      alert('New admin set successfully');
    } catch (err) {
      alert('Failed to set new admin: ' + err.message);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!userData) return;
    
    try {
      // For non-admins, only allow password updates
      if (companyData.adminUserId !== auth.currentUser.uid) {
        if (!password) {
          alert('Please enter a new password');
          return;
        }
        if (password !== confirmPassword) {
          alert('Passwords do not match');
          return;
        }
        if (password.length < 6) {
          alert('Password must be at least 6 characters long');
          return;
        }
      await updatePassword(auth.currentUser, password);
        setPassword(''); // Clear password fields
        setConfirmPassword('');
        alert('Password updated successfully');
        return;
      }
      
      // For admins, handle password updates
      if (password) {
        if (password !== confirmPassword) {
          alert('Passwords do not match');
          return;
        }
        if (password.length < 6) {
          alert('Password must be at least 6 characters long');
          return;
        }
        await updatePassword(auth.currentUser, password);
        setPassword(''); // Clear password fields
        setConfirmPassword('');
        alert('Password updated successfully');
        return;
      }
      
      // Handle other admin updates (if any)
      if (email) {
        await updateEmail(auth.currentUser, email);
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { email });
      alert('Profile updated successfully');
      }
    } catch (err) {
      alert('Failed to update profile: ' + err.message);
    }
  };

  const generatePassword = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 5);
  };

  const handleSubscribeToLicenses = async (licenseCount) => {
    // Show custom confirmation modal explaining the process
    const modalData = {
      licenseCount,
      totalCost: licenseCount * 50,
      onConfirm: () => proceedWithSubscription(licenseCount),
      onCancel: () => setShowConfirmModal(false)
    };
    
    setConfirmModalData(modalData);
    setShowConfirmModal(true);
  };

  const proceedWithSubscription = async (licenseCount) => {
    setShowConfirmModal(false);

    setPurchaseStatus('Creating subscription...');
    try {
      console.log('Creating PayPal subscription for:', {
        companyId: companyData.id,
        licenseCount
      });
      
      // Use the callable function instead of HTTP request
      const createSubscription = httpsCallable(functions, 'createPayPalSubscription');
      const result = await createSubscription({
          companyId: companyData.id,
        licenseCount: licenseCount,
        adminEmail: auth.currentUser.email,
        adminName: userData?.name || 'Admin'
      });
      
      console.log('PayPal subscription response:', result.data);
      
      if (result.data.success && result.data.approvalUrl) {
        setPurchaseStatus('Redirecting to PayPal...');
        // Redirect to PayPal for approval
        window.location.href = result.data.approvalUrl;
      } else {
        setPurchaseStatus(result.data.error || 'Failed to create subscription');
        console.error('Subscription error:', result.data.error);
      }
    } catch (err) {
      setPurchaseStatus('Error creating subscription: ' + (err.message || err));
      console.error('Subscription error:', err.message || err);
    }
  };

  const handleDebugData = async () => {
    try {
      const debugData = httpsCallable(functions, 'debugData');
      const result = await debugData({});
      console.log('Debug data result:', result.data);
      alert(JSON.stringify(result.data, null, 2));
    } catch (err) {
      console.error('Debug error:', err);
      alert('Debug error: ' + err.message);
    }
  };



  const handleManageSubscription = () => {
    if (!companyData?.paypalSubscriptionId) {
      alert('No active PayPal subscription found.');
      return;
    }
    
    // Redirect to PayPal subscription management
    const paypalManageUrl = `https://www.sandbox.paypal.com/myaccount/autopay/connect/${companyData.paypalSubscriptionId}`;
    window.open(paypalManageUrl, '_blank');
  };

  const handleCancelIndividualSubscription = (subscriptionId, licenseId) => {
    // Find the license to check if it's an admin license
    const license = companyData.licenses?.find(l => l.id === licenseId);
    const isAdminLicense = license?.admin;
    const totalLicenses = companyData.licenses?.length || 0;
    const otherLicenses = companyData.licenses?.filter(l => l.id !== licenseId) || [];
    const hasOtherAssignedLicenses = otherLicenses.some(l => l.assigned && !l.admin);
    
    // Show custom confirmation modal for cancellation
    const cancelModalData = {
      type: 'cancel',
      licenseId,
      subscriptionId,
      isAdminLicense,
      hasOtherAssignedLicenses,
      totalLicenses,
      onConfirm: () => proceedWithCancellation(subscriptionId),
      onCancel: () => setShowConfirmModal(false)
    };
    
    setConfirmModalData(cancelModalData);
    setShowConfirmModal(true);
  };

  const proceedWithCancellation = (subscriptionId) => {
    setShowConfirmModal(false);
    // Redirect directly to PayPal subscription management for this specific subscription
    const paypalCancelUrl = `https://www.sandbox.paypal.com/myaccount/autopay/connect/${subscriptionId}`;
    window.location.href = paypalCancelUrl;
  };

  // Super Admin: Handle manual license creation for any company (FREE ONLY)
  const handleSuperAdminCreateLicenses = async () => {
    if (!targetCompanyId || !manualLicenseCount || manualLicenseCount < 1) {
      alert('Please enter a valid company ID and number of licenses');
      return;
    }

    try {
      const createManualLicenses = httpsCallable(functions, 'createManualLicenses');
      const result = await createManualLicenses({
        companyId: targetCompanyId,
        licenseCount: manualLicenseCount,
        licenseType: 'free', // Always free - no expiration, no auto-deletion
        expirationDate: null,
                notes: manualNotes || `Super admin created manual licenses - no expiration`
      });
      
      if (result.data.success) {
        alert(`✅ Successfully created ${result.data.licensesCreated} license(s) for company ${targetCompanyId}!\n\nLicense IDs: ${result.data.licenseIds.join(', ')}\n\n🔒 These licenses will never expire automatically.`);
        
        // Reset form
        setTargetCompanyId('');
        setManualLicenseCount(1);
        setManualNotes('');
      }
    } catch (error) {
      console.error('Super admin license creation error:', error);
      alert('❌ Error creating licenses: ' + error.message);
    }
  };

    // Super Admin: Handle manual license deletion
  const handleSuperAdminDeleteLicense = async () => {
    if (!targetCompanyId || !licenseToDelete) {
      alert('Please enter both company ID and license ID');
      return;
    }

    if (!window.confirm(`⚠️ DANGER: Delete license "${licenseToDelete}" from company "${targetCompanyId}"?\n\n🚨 This will:\n- Remove the license permanently\n- Kick out any assigned user\n- Delete their user account\n\nThis action CANNOT be undone!`)) {
      return;
    }

    try {
      const deleteManualLicense = httpsCallable(functions, 'deleteManualLicense');
      const result = await deleteManualLicense({
        companyId: targetCompanyId,
        licenseId: licenseToDelete
      });
      
      if (result.data.success) {
        alert(`✅ Successfully deleted license "${licenseToDelete}" from company ${targetCompanyId}!\n\n${result.data.message}`);
        
        // Reset form
        setTargetCompanyId('');
        setLicenseToDelete('');
      }
    } catch (error) {
      console.error('Super admin license deletion error:', error);
      alert('❌ Error deleting license: ' + error.message);
    }
  };

  // Super Admin: Handle grace period extension
  const handleExtendGracePeriod = async () => {
    if (!extensionCompanyId.trim()) {
      alert('Please enter a Company ID');
      return;
    }

    if (extensionDays < 1 || extensionDays > 30) {
      alert('Extension days must be between 1 and 30');
      return;
    }

    try {
      console.log('Extending grace period for company:', extensionCompanyId.trim());
      
      const extendLicenseGracePeriod = httpsCallable(functions, 'extendLicenseGracePeriod');
      const result = await extendLicenseGracePeriod({
        companyId: extensionCompanyId.trim(),
        extensionDays: parseInt(extensionDays),
        reason: extensionReason.trim() || 'Manual extension by admin'
      });

      if (result.data.success) {
        alert(`✅ Success!\n${result.data.message}\nCompany: ${result.data.companyName}\nLicenses Updated: ${result.data.licensesUpdated}\nNew Grace Period End: ${new Date(result.data.newGracePeriodEnd).toLocaleString()}`);
        
        // Clear form
        setExtensionCompanyId('');
        setExtensionDays(7);
        setExtensionReason('');
      } else {
        alert('❌ Failed to extend grace period: ' + result.data.message);
      }
    } catch (error) {
      console.error('Error extending grace period:', error);
      alert('❌ Error extending grace period: ' + error.message);
    }
  };

  // Super Admin: Fetch all companies for dropdown selection
  const fetchAllCompanies = async () => {
    if (allCompanies.length > 0) return; // Already loaded
    
    setCompaniesLoading(true);
    try {
      const companiesQuery = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesQuery);
      
      const companies = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.data().companyName || 'Unknown Company',
        ...doc.data()
      }));
      
      // Sort companies by name for easier selection
      companies.sort((a, b) => a.name.localeCompare(b.name));
      
      setAllCompanies(companies);
      console.log('📋 Loaded', companies.length, 'companies for selection');
    } catch (error) {
      console.error('Error fetching companies:', error);
      alert('❌ Error loading companies: ' + error.message);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // Super Admin: Handle company selection from dropdown
  const handleCompanySelect = (companyId) => {
    setTargetCompanyId(companyId);
    const selectedCompany = allCompanies.find(c => c.id === companyId);
    console.log('🏢 Selected company:', selectedCompany?.name, '(ID:', companyId, ')');
  };



  // Super Admin: Handle company creation
  const handleSuperAdminCreateCompany = async () => {
    if (!newCompanyName || !newAdminEmail || !newAdminName || !newAdminPassword || !initialLicenseCount) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (newAdminPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    if (!window.confirm(`Create new company?\n\nCompany: ${newCompanyName}\nAdmin: ${newAdminName} (${newAdminEmail})\nPassword: ${newAdminPassword}\nLicenses: ${initialLicenseCount}\n\nThis will create the company and admin user account.`)) {
      return;
    }

    try {
      const createCompanyWithAdmin = httpsCallable(functions, 'createCompanyWithAdmin');
      const result = await createCompanyWithAdmin({
        companyName: newCompanyName,
        adminEmail: newAdminEmail,
        adminName: newAdminName,
        adminPassword: newAdminPassword,
        initialLicenseCount: initialLicenseCount,
        notes: `Super admin created company with ${initialLicenseCount} licenses`
      });
      
      if (result.data.success) {
        alert(`✅ Successfully created company "${newCompanyName}"!\n\nCompany ID: ${result.data.companyId}\nAdmin Login:\n📧 Email: ${newAdminEmail}\n🔑 Password: ${newAdminPassword}\n\nLicenses: ${result.data.licenseIds.join(', ')}\n\n🎯 The admin can now log in immediately!`);
        
        // Reset form
        setNewCompanyName('');
        setNewAdminEmail('');
        setNewAdminName('');
        setNewAdminPassword('');
        setInitialLicenseCount(1);
      }
    } catch (error) {
      console.error('Super admin company creation error:', error);
      alert('❌ Error creating company: ' + error.message);
    }
  };





  if (loading) return <div style={{ color: '#e0e0e0', textAlign: 'center', padding: '20px' }}>Loading...</div>;
  if (error) return <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{error}</div>;
  if (!userData || !companyData) return <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>No data found.</div>;

  // Calculate available licenses, always count admin's license as assigned
  const totalLicenses = companyData.licenses.length;
  const adminLicenseIds = companyData.licenses
    .filter(license => license.admin || license.email === auth.currentUser.email)
    .map(license => license.id);
  const availableLicenses = companyData.licenses.filter(
    license => !license.assigned && !adminLicenseIds.includes(license.id)
  ).length;
  const availableLicenseList = companyData.licenses.filter(license => !license.assigned);

  // Helper: Merge members and licenses for table
  // Build memberRows from licenses, not users, so all invited/assigned users show up
  const memberRows = companyData.licenses
    .filter(license => license.email) // Only show licenses with an email (invited/assigned)
    .map(license => {
      const user = membersData.find(u => u.email === license.email);
      const isAdmin = user ? user.uid === companyData.adminUserId : false;
      return {
        name: license.name || (user ? user.name : ''),
        email: license.email,
        status: isAdmin ? 'Active (Admin)' : (license.status === 'invited' ? 'Invited' : 
                license.status === 'cancelled' ? `Cancelled (Active until ${new Date(license.expirationDate).toLocaleDateString()})` :
                license.status === 'suspended' ? `Suspended (Active until ${new Date(license.expirationDate).toLocaleDateString()})` : 'Active'),
        inviteToken: license.inviteToken,
        assigned: license.assigned,
        uid: user ? user.uid : null,
        isAdmin,
        licenseId: license.id,
      };
    });
  // Ensure admin is always present
  if (!memberRows.some(row => row.uid === companyData.adminUserId)) {
    const adminUser = membersData.find(m => m.uid === companyData.adminUserId);
    if (adminUser) {
      memberRows.push({ ...adminUser, status: 'Admin', assigned: true });
    }
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Poppins, sans-serif', color: '#e0e0e0', background: '#2d3748' }}>
      <h2>Account Settings</h2>

             {/* Super Admin Panel - Available to all super admins regardless of company role */}
       {isSuperAdmin && (
         <div style={{ 
           marginBottom: '30px', 
           padding: '20px', 
           border: '2px solid #dc2626', 
           borderRadius: '8px', 
           background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
         }}>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
             <div>
               <h3 style={{ margin: '0', color: '#dc2626', fontSize: '18px', fontWeight: 'bold' }}>
                 🛡️ Super Admin Controls
               </h3>
               <p style={{ margin: '5px 0 0 0', color: '#9ca3af', fontSize: '13px' }}>
                 Global system administration - Use with caution
               </p>
             </div>
             <button
               onClick={() => setShowSuperAdminPanel(!showSuperAdminPanel)}
               style={{
                 padding: '8px 16px',
                 background: showSuperAdminPanel ? '#dc2626' : '#4a5568',
                 color: '#fff',
                 border: 'none',
                 borderRadius: '6px',
                 cursor: 'pointer',
                 fontSize: '12px',
                 fontWeight: 'bold'
               }}
             >
               {showSuperAdminPanel ? '🔒 Hide Panel' : '🔓 Show Panel'}
             </button>
           </div>

           {showSuperAdminPanel && (
                <div style={{ 
                  border: '1px solid #4a5568', 
                  borderRadius: '8px', 
                  padding: '20px',
                  background: '#2d3748'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    
                    {/* CREATE NEW COMPANY - FIRST */}
                    <div>
                      <h4 style={{ margin: '0 0 15px 0', color: '#3b82f6' }}>🏢 Create New Company</h4>
                      <p style={{ margin: '0 0 15px 0', color: '#a0aec0', fontSize: '13px' }}>
                        Create a new company with admin user and initial licenses.
                      </p>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '5px', fontSize: '14px' }}>
                          Company Name: *
                        </label>
                        <input 
                          type="text" 
                          value={newCompanyName} 
                          onChange={e => setNewCompanyName(e.target.value)}
                          placeholder="e.g., ACME Corp"
                          style={{ 
                            width: '100%',
                            padding: '8px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '4px',
                    fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '5px', fontSize: '14px' }}>
                          Admin Email: *
                        </label>
                        <input 
                          type="email" 
                          value={newAdminEmail} 
                          onChange={e => setNewAdminEmail(e.target.value)}
                          placeholder="admin@company.com"
                          style={{ 
                            width: '100%',
                            padding: '8px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '5px', fontSize: '14px' }}>
                          Admin Name: *
                        </label>
                        <input 
                          type="text" 
                          value={newAdminName} 
                          onChange={e => setNewAdminName(e.target.value)}
                          placeholder="John Smith"
                          style={{ 
                            width: '100%',
                            padding: '8px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '5px', fontSize: '14px' }}>
                          Admin Password: *
                        </label>
                        <input 
                          type="password" 
                          value={newAdminPassword} 
                          onChange={e => setNewAdminPassword(e.target.value)}
                          placeholder="Min 6 characters"
                          style={{ 
                            width: '100%',
                            padding: '8px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '5px', fontSize: '14px' }}>
                          Initial Licenses: *
                        </label>
                        <input 
                          type="number" 
                          min="1" 
                          max="1000"
                          value={initialLicenseCount} 
                          onChange={e => setInitialLicenseCount(Number(e.target.value))} 
                          style={{ 
                            width: '100%',
                            padding: '8px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <button
                        onClick={handleSuperAdminCreateCompany}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: '#3b82f6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        🏢 Create Company
                </button>
                    </div>

                    {/* CREATE MANUAL LICENSES - SECOND */}
                    <div>
                      <h4 style={{ margin: '0 0 15px 0', color: '#10b981' }}>➕ Create Manual Licenses</h4>
                      <p style={{ margin: '0 0 15px 0', color: '#a0aec0', fontSize: '13px' }}>
                        Manually add licenses for any company - these never expire automatically.
                      </p>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Select Company: *
                        </label>
                        <select
                          value={targetCompanyId}
                          onChange={e => handleCompanySelect(e.target.value)}
                          onFocus={fetchAllCompanies}
                          disabled={companiesLoading}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#4a5568',
                            color: '#e0e0e0',
                            border: '1px solid #4a5568',
                            borderRadius: '5px',
                            fontSize: '14px',
                            cursor: companiesLoading ? 'wait' : 'pointer'
                          }}
                        >
                          <option value="">
                            {companiesLoading ? '⏳ Loading companies...' : '📋 Select a company...'}
                          </option>
                          {allCompanies.map(company => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                        {targetCompanyId && (
                          <div style={{ 
                            marginTop: '8px', 
                            padding: '8px 12px', 
                            background: '#065f46',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#a7f3d0'
                          }}>
                            <strong>Selected:</strong> {allCompanies.find(c => c.id === targetCompanyId)?.name || 'Unknown'} 
                            <br />
                            <strong>ID:</strong> <code style={{ background: '#1f2937', padding: '2px 4px', borderRadius: '3px' }}>{targetCompanyId}</code>
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Number of Licenses: *
                        </label>
                        <input 
                          type="number" 
                          min="1" 
                          max="1000"
                          value={manualLicenseCount} 
                          onChange={e => setManualLicenseCount(Number(e.target.value))} 
                          style={{ 
                            width: '100%',
                            padding: '10px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '5px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Notes (Optional):
                        </label>
                        <textarea 
                          value={manualNotes} 
                          onChange={e => setManualNotes(e.target.value)}
                          placeholder="e.g., Enterprise deal for ACME Corp, etc."
                          rows={2}
                          style={{ 
                            width: '100%',
                            padding: '10px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '5px',
                            fontSize: '14px',
                            resize: 'vertical'
                          }}
                        />
                      </div>

              <button 
                        onClick={handleSuperAdminCreateLicenses}
                style={{ 
                          width: '100%',
                          padding: '12px 20px',
                          background: '#10b981',
                  color: '#fff', 
                  border: 'none', 
                          borderRadius: '6px',
                  cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                }}
              >
                        ➕ Create {manualLicenseCount} License{manualLicenseCount !== 1 ? 's' : ''}
              </button>
                    </div>

                    {/* DELETE LICENSE - THIRD */}
                    <div>
                      <h4 style={{ margin: '0 0 15px 0', color: '#dc2626' }}>🗑️ Delete License</h4>
                      <p style={{ margin: '0 0 15px 0', color: '#a0aec0', fontSize: '13px' }}>
                        Manually remove specific licenses - use when needed for cleanup.
                      </p>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Select Company: *
                        </label>
                        <select
                          value={targetCompanyId}
                          onChange={e => handleCompanySelect(e.target.value)}
                          onFocus={fetchAllCompanies}
                          disabled={companiesLoading}
                  style={{ 
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#4a5568',
                            color: '#e0e0e0',
                            border: '1px solid #4a5568',
                            borderRadius: '5px',
                            fontSize: '14px',
                            cursor: companiesLoading ? 'wait' : 'pointer'
                          }}
                        >
                          <option value="">
                            {companiesLoading ? '⏳ Loading companies...' : '📋 Select a company...'}
                          </option>
                          {allCompanies.map(company => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                        {targetCompanyId && (
                          <div style={{ 
                            marginTop: '8px', 
                    padding: '8px 12px', 
                            background: '#065f46',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#a7f3d0'
                          }}>
                            <strong>Selected:</strong> {allCompanies.find(c => c.id === targetCompanyId)?.name || 'Unknown'} 
                            <br />
                            <strong>ID:</strong> <code style={{ background: '#1f2937', padding: '2px 4px', borderRadius: '3px' }}>{targetCompanyId}</code>
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          License ID to Delete: *
                        </label>
                        <input 
                          type="text" 
                          value={licenseToDelete} 
                          onChange={e => setLicenseToDelete(e.target.value)}
                          placeholder="e.g., ZENTEKCO-07"
                          style={{ 
                            width: '100%',
                            padding: '10px', 
                            backgroundColor: '#4a5568', 
                            color: '#e0e0e0', 
                            border: '1px solid #4a5568', 
                            borderRadius: '5px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px', padding: '10px', background: '#7f1d1d', borderRadius: '5px' }}>
                        <p style={{ margin: '0', color: '#fca5a5', fontSize: '12px' }}>
                          ⚠️ WARNING: This permanently deletes the license and kicks out any assigned user!
                        </p>
                      </div>

                      <button
                        onClick={handleSuperAdminDeleteLicense}
                        style={{
                          width: '100%',
                          padding: '12px 20px',
                          background: '#dc2626',
                    color: '#fff', 
                    border: 'none', 
                          borderRadius: '6px',
                    cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                  }}
                >
                        🗑️ DELETE License
                </button>
                    </div>

                    {/* EXTEND GRACE PERIOD - FOURTH */}
                    <div>
                      <h4 style={{ margin: '0 0 15px 0', color: '#f59e0b' }}>⏰ Extend Grace Period</h4>
                      <p style={{ margin: '0 0 15px 0', color: '#a0aec0', fontSize: '13px' }}>
                        Extend grace period for companies with payment failures - use for customer service.
                      </p>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Company ID: *
                        </label>
                        <input
                          type="text"
                          value={extensionCompanyId}
                          onChange={(e) => setExtensionCompanyId(e.target.value)}
                          placeholder="Enter company ID..."
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#4a5568',
                            color: '#e0e0e0',
                            border: '1px solid #4a5568',
                            borderRadius: '5px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Extension Days (1-30): *
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={extensionDays}
                          onChange={(e) => setExtensionDays(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#4a5568',
                            color: '#e0e0e0',
                            border: '1px solid #4a5568',
                            borderRadius: '5px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                          Reason (optional):
                        </label>
                        <input
                          type="text"
                          value={extensionReason}
                          onChange={(e) => setExtensionReason(e.target.value)}
                          placeholder="Customer service extension..."
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#4a5568',
                            color: '#e0e0e0',
                            border: '1px solid #4a5568',
                            borderRadius: '5px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ 
                        background: '#f59e0b20', 
                        border: '1px solid #f59e0b40', 
                        borderRadius: '6px', 
                        padding: '12px', 
                        marginBottom: '15px' 
                      }}>
                        <p style={{ margin: 0, color: '#f59e0b', fontSize: '13px' }}>
                          ⚠️ This will extend the grace period and restore access for suspended accounts.
                        </p>
                      </div>

                      <button
                        onClick={handleExtendGracePeriod}
                        style={{
                          width: '100%',
                          padding: '12px 20px',
                          background: '#f59e0b',
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        ⏰ EXTEND Grace Period
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

       {companyData.adminUserId === auth.currentUser.uid ? (
         <div>
           <h3>Admin Controls</h3>
           <p>Company: {companyData.name}</p>

           {/* Regular Admin Controls (Company-specific) */}
           <div style={{ marginBottom: '20px' }}>
            <h4>License Overview</h4>
            <div style={{ 
              background: '#4a5568', 
              padding: '15px', 
              borderRadius: '8px', 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center' 
            }}>
              <div>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                  {availableLicenses} of {totalLicenses} licenses available
                </span>
                <p style={{ fontSize: '14px', color: '#a0aec0', margin: '5px 0 0 0' }}>
                  Each license costs $50/month and can be managed individually below.
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#48bb78' }}>
                  ${totalLicenses * 50}/month
                </div>
                <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                  Total subscription cost
                </div>
              </div>
            </div>
          </div>
          
          {/* Individual License Management */}
          <div style={{ marginBottom: '20px' }}>
            <h4>License Management</h4>
            <p style={{ fontSize: '14px', color: '#a0aec0', marginBottom: '15px' }}>
              Each license is a separate $50/month PayPal subscription. You can cancel individual licenses as needed.
            </p>
            
            <div style={{ background: '#2d3748', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#4a5568' }}>
                    <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>License ID</th>
                    <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Assigned To</th>
                    <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Subscription</th>
                    <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companyData.licenses?.map(license => {
                    const isAssigned = license.assigned;
                    // Check if this license is assigned to the current admin
                    const assignedToAdmin = license.email && membersData.find(member => 
                      member.email === license.email && member.uid === companyData.adminUserId
                    );
                    const assignedUser = isAssigned 
                      ? (assignedToAdmin ? `${license.email} (Admin)` : license.email) 
                      : 'Available';
                    
                    return (
                      <tr key={license.id} style={{ background: '#23263a' }}>
                        <td style={{ padding: '10px', border: '1px solid #4a5568', fontSize: '12px' }}>
                          {license.id}
                        </td>
                        <td style={{ padding: '10px', border: '1px solid #4a5568' }}>
                          {assignedUser}
                        </td>
                        <td style={{ padding: '10px', border: '1px solid #4a5568' }}>
                          <span style={{ 
                            padding: '4px 8px', 
                            borderRadius: '12px', 
                            fontSize: '12px',
                            background: 
                              license.status === 'cancelled' ? '#f56565' :
                              license.status === 'suspended' ? '#f6ad55' :
                              isAssigned ? '#48bb78' : '#ed8936',
                            color: 'white',
                            display: 'inline-block'
                          }}>
                            {license.status === 'cancelled' 
                              ? `Cancelled (expires ${new Date(license.expirationDate).toLocaleDateString()})`
                              : license.status === 'suspended' 
                              ? `Suspended (expires ${new Date(license.expirationDate).toLocaleDateString()})`
                              : isAssigned ? 'In Use' : 'Available'
                            }
                          </span>
                        </td>
                        <td style={{ padding: '10px', border: '1px solid #4a5568', fontSize: '12px' }}>
                          $50/month
                        </td>
                        <td style={{ padding: '10px', border: '1px solid #4a5568' }}>
                          {license.subscriptionId ? (
              <button 
                              onClick={() => handleCancelIndividualSubscription(license.subscriptionId, license.id)}
                style={{ 
                                padding: '6px 12px', 
                                background: '#e53e3e', 
                                color: '#fff', 
                  border: 'none', 
                                borderRadius: '4px', 
                  cursor: 'pointer',
                                fontSize: '12px'
                }}
              >
                              Cancel License
              </button>
                          ) : (
                            <span style={{ color: '#a0aec0', fontSize: '12px' }}>No subscription</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <h4>Assign License</h4>
            <form onSubmit={handleAssignLicense} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <select
                value={selectedLicense ? selectedLicense.id : ''}
                onChange={(e) => setSelectedLicense(companyData.licenses.find(license => license.id === e.target.value))}
                style={{ padding: '10px', backgroundColor: '#4a5568', color: '#e0e0e0', border: '1px solid #4a5568', borderRadius: '5px' }}
              >
                <option value="">Select Available License</option>
                {companyData.licenses.filter(license => !license.assigned && !adminLicenseIds.includes(license.id) && license.status !== 'invited').map(license => (
                  <option key={license.id} value={license.id}>{license.id} {license.expirationDate ? `(Expires: ${new Date(license.expirationDate).toLocaleDateString()})` : ''}</option>
                ))}
              </select>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Email"
                required
                style={{ padding: '10px', backgroundColor: '#4a5568', color: '#e0e0e0', border: '1px solid #4a5568', borderRadius: '5px' }}
              />
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name"
                required
                style={{ padding: '10px', backgroundColor: '#4a5568', color: '#e0e0e0', border: '1px solid #4a5568', borderRadius: '5px' }}
              />
              <button type="submit" disabled={!selectedLicense || !newEmail || !newName} style={{ padding: '10px 15px', background: 'linear-gradient(90deg, #4A90E2 0%, #357ABD 100%)', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', transition: 'background 0.3s' }}>Assign License</button>
            </form>
          </div>
          <div style={{ marginBottom: '20px', marginTop: '20px' }}>
            <h4>Subscribe to Licenses</h4>
            {paypalSuccess && (
              <div style={{ 
                background: '#4caf50', 
                color: 'white', 
                padding: '10px', 
                borderRadius: '5px', 
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                ✅ {paypalSuccess}
              </div>
            )}
            <p style={{ color: '#ccc', fontSize: '14px', marginBottom: '15px' }}>
              Monthly subscription - $50 per license per month. Cancel anytime through PayPal.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: 500 }}>
              {/* Streamlined subscription interface */}
              <div style={{ 
                border: '1px solid #4a5568', 
                borderRadius: '8px', 
                padding: '20px',
                background: '#3a4556'
              }}>
                <h5 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#e0e0e0' }}>Subscribe to Licenses</h5>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px' }}>
                      Number of licenses needed:
                    </label>
                  <input 
                    type="number" 
                    min="1" 
                    max="100"
                    value={purchaseCount} 
                    onChange={e => setPurchaseCount(Number(e.target.value))} 
                      placeholder="Enter quantity" 
                    style={{ 
                        width: '100%',
                        padding: '10px', 
                      backgroundColor: '#4a5568', 
                      color: '#e0e0e0', 
                      border: '1px solid #4a5568', 
                        borderRadius: '5px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                  <div style={{ textAlign: 'center', minWidth: '120px' }}>
                    <div style={{ color: '#4A90E2', fontSize: '20px', fontWeight: 'bold' }}>
                      ${purchaseCount * 50}/month
                    </div>
                    <div style={{ color: '#ccc', fontSize: '12px' }}>
                      $50 per license
                    </div>
                  </div>
                  <button 
                    onClick={() => handleSubscribeToLicenses(purchaseCount)}
                    style={{ 
                      padding: '12px 24px', 
                      background: 'linear-gradient(90deg, #4A90E2 0%, #357ABD 100%)', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px', 
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      minWidth: '120px'
                    }}
                  >
                    Subscribe
                  </button>
                </div>
              </div>
              
              {purchaseStatus && (
                <div style={{ 
                  padding: '10px', 
                  background: (typeof purchaseStatus === 'string' && (purchaseStatus.includes('error') || purchaseStatus.includes('failed'))) ? '#ff5555' : '#4CAF50',
                  borderRadius: '5px',
                  color: '#fff'
                }}>
                  {purchaseStatus}
                </div>
              )}
            </div>
          </div>



          {/* Admin Password Change Section */}
          <div style={{ marginBottom: '30px' }}>
            <h4>Change Your Password</h4>
            <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '400px' }}>
              <div>
                <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ 
                    padding: '12px', 
                    backgroundColor: '#4a5568', 
                    color: '#e0e0e0', 
                    border: '1px solid #4a5568', 
                    borderRadius: '8px',
                    width: '100%'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{ 
                    padding: '12px', 
                    backgroundColor: '#4a5568', 
                    color: '#e0e0e0', 
                    border: confirmPassword && password !== confirmPassword ? '1px solid #e53e3e' : '1px solid #4a5568', 
                    borderRadius: '8px',
                    width: '100%'
                  }}
                />
                {confirmPassword && password !== confirmPassword && (
                  <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '5px', marginBottom: '0' }}>
                    Passwords do not match
                  </p>
                )}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <p style={{ fontSize: '12px', color: '#a0aec0', margin: '0' }}>
                  Password must be at least 6 characters long
                </p>
              </div>
              <button 
                type="submit" 
                disabled={!password || password.length < 6 || password !== confirmPassword}
                style={{ 
                  padding: '12px 24px', 
                  background: (!password || password.length < 6 || password !== confirmPassword) ? '#6b7280' : 'linear-gradient(90deg, #4A90E2 0%, #357ABD 100%)', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: (!password || password.length < 6 || password !== confirmPassword) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '200px'
                }}
              >
                Update Password
              </button>
            </form>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <span style={{ fontWeight: 'bold' }}>Set New Admin:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
              <select
                value={newAdminId}
                onChange={(e) => setNewAdminId(e.target.value)}
                style={{ padding: '10px', backgroundColor: '#4a5568', color: '#e0e0e0', border: '1px solid #4a5568', borderRadius: '5px', height: '44px' }}
              >
                <option value="">Select New Admin</option>
                {membersData.filter(member => member.uid !== auth.currentUser.uid).map(member => (
                  <option key={member.uid} value={member.uid}>{member.name} ({member.email})</option>
                ))}
              </select>
              <button
                onClick={handleSetNewAdmin}
                disabled={!newAdminId}
                style={{ height: '44px', padding: '0 24px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Confirm New Admin
              </button>
            </div>
          </div>
          <div>
            <h4>Company Members</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
              <thead>
                <tr style={{ background: '#4a5568' }}>
                  <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '10px', border: '1px solid #e0e0e0', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {memberRows.map(row => {
                  const inviteUrl = row.inviteToken ? `${window.location.origin}/invite?token=${row.inviteToken}&companyId=${companyData.id}&licenseId=${row.licenseId}` : '';
                  return (
                    <tr key={row.licenseId || row.uid || row.email} style={{ background: '#2d3748' }}>
                      <td style={{ padding: '10px', border: '1px solid #e0e0e0' }}>{row.name}</td>
                      <td style={{ padding: '10px', border: '1px solid #e0e0e0' }}>{row.email}</td>
                      <td style={{ padding: '10px', border: '1px solid #e0e0e0' }}>{row.status}</td>
                      <td style={{ padding: '10px', border: '1px solid #e0e0e0' }}>
                        {row.status === 'Invited' && inviteUrl && (
                          <button onClick={() => {navigator.clipboard.writeText(inviteUrl); alert('Invite link copied!')}} style={{ padding: '5px 10px', background: '#4A90E2', color: '#fff', border: 'none', borderRadius: '5px', marginRight: '5px', cursor: 'pointer' }}>Copy Invite Link</button>
                        )}
                        {/* Show Unassign for all invited users, and for active users who are not the current user or admin */}
                        {(row.status === 'Invited' || (row.uid && row.uid !== auth.currentUser.uid && row.status !== 'Admin')) && (
                          <button onClick={() => openModal('unassign', row)} style={{ padding: '5px 10px', background: '#ff5555', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Unassign</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Modal Dialogs */}
          {modal.open && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#23263a', padding: 32, borderRadius: 8, minWidth: 320, maxWidth: 400 }}>
                {modal.type === 'edit' && (
                  <>
                    <h3>Edit User</h3>
                    <input value={modalName} onChange={e => setModalName(e.target.value)} placeholder="Name" style={{ width: '100%', marginBottom: 12, padding: 8, borderRadius: 4, border: '1px solid #4a5568', background: '#2d3748', color: '#e0e0e0' }} />
                    <input value={modalEmail} onChange={e => setModalEmail(e.target.value)} placeholder="Email" style={{ width: '100%', marginBottom: 12, padding: 8, borderRadius: 4, border: '1px solid #4a5568', background: '#2d3748', color: '#e0e0e0' }} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={closeModal} style={{ padding: '8px 16px', background: '#888', color: '#fff', border: 'none', borderRadius: 4 }}>Cancel</button>
                      <button onClick={handleEditUser} style={{ padding: '8px 16px', background: '#4A90E2', color: '#fff', border: 'none', borderRadius: 4 }}>Save</button>
                    </div>
                  </>
                )}
                {modal.type === 'reassign' && (
                  <>
                    <h3>Reassign License</h3>
                    <input value={modalName} onChange={e => setModalName(e.target.value)} placeholder="New Name" style={{ width: '100%', marginBottom: 12, padding: 8, borderRadius: 4, border: '1px solid #4a5568', background: '#2d3748', color: '#e0e0e0' }} />
                    <input value={modalEmail} onChange={e => setModalEmail(e.target.value)} placeholder="New Email" style={{ width: '100%', marginBottom: 12, padding: 8, borderRadius: 4, border: '1px solid #4a5568', background: '#2d3748', color: '#e0e0e0' }} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={closeModal} style={{ padding: '8px 16px', background: '#888', color: '#fff', border: 'none', borderRadius: 4 }}>Cancel</button>
                      <button onClick={handleReassignLicense} style={{ padding: '8px 16px', background: '#ffaa00', color: '#fff', border: 'none', borderRadius: 4 }}>Reassign</button>
                    </div>
                  </>
                )}
                {modal.type === 'unassign' && (
                  <>
                    <h3>Unassign User</h3>
                    <p>Are you sure you want to unassign this user?</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={closeModal} style={{ padding: '8px 16px', background: '#888', color: '#fff', border: 'none', borderRadius: 4 }}>Cancel</button>
                      <button onClick={handleUnassignUser} style={{ padding: '8px 16px', background: '#ff5555', color: '#fff', border: 'none', borderRadius: 4 }}>Unassign</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h3>Your Account</h3>
          <p style={{ color: '#a0aec0', marginBottom: '30px' }}>
            Company: <span style={{ color: '#e0e0e0', fontWeight: 'bold' }}>{companyData.name}</span>
          </p>

          {/* User Profile Section */}
          <div style={{ marginBottom: '30px' }}>
            <h4>Profile Information</h4>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={userData?.name || ''}
                  readOnly
                  style={{ 
                    padding: '12px', 
                    backgroundColor: '#4a5568', 
                    color: '#a0aec0', 
                    border: '1px solid #4a5568', 
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '400px',
                    cursor: 'not-allowed'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '5px', marginBottom: '0' }}>
                  Name is tied to your license and cannot be changed
                </p>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Email
                </label>
              <input
                type="email"
                  value={userData?.email || ''}
                  readOnly
                  style={{ 
                    padding: '12px', 
                    backgroundColor: '#4a5568', 
                    color: '#a0aec0', 
                    border: '1px solid #4a5568', 
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '400px',
                    cursor: 'not-allowed'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '5px', marginBottom: '0' }}>
                  Email is tied to your license and cannot be changed
                </p>
            </div>
            </div>
            
            <h4>Change Password</h4>
            <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
                <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  New Password
                </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ 
                    padding: '12px', 
                    backgroundColor: '#4a5568', 
                    color: '#e0e0e0', 
                    border: '1px solid #4a5568', 
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '400px'
                  }}
              />
            </div>
              <div>
                <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{ 
                    padding: '12px', 
                    backgroundColor: '#4a5568', 
                    color: '#e0e0e0', 
                    border: confirmPassword && password !== confirmPassword ? '1px solid #e53e3e' : '1px solid #4a5568', 
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '400px'
                  }}
                />
                {confirmPassword && password !== confirmPassword && (
                  <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '5px', marginBottom: '0' }}>
                    Passwords do not match
                  </p>
                )}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <p style={{ fontSize: '12px', color: '#a0aec0', margin: '0' }}>
                  Password must be at least 6 characters long
                </p>
              </div>
              <button 
                type="submit" 
                disabled={!password || password.length < 6 || password !== confirmPassword}
                style={{ 
                  padding: '12px 24px', 
                  background: (!password || password.length < 6 || password !== confirmPassword) ? '#6b7280' : 'linear-gradient(90deg, #4A90E2 0%, #357ABD 100%)', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: (!password || password.length < 6 || password !== confirmPassword) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '200px'
                }}
              >
                Update Password
              </button>
          </form>
          </div>

          {/* License Information */}
          <div style={{ marginBottom: '30px' }}>
            <h4>Your License</h4>
            <div style={{ 
              background: '#4a5568', 
              padding: '20px', 
              borderRadius: '8px',
              maxWidth: '500px'
            }}>
              {(() => {
                const userLicense = companyData.licenses?.find(license => 
                  license.email === userData?.email || license.email === auth.currentUser?.email
                );
                
                if (userLicense) {
                  return (
                    <>
                      <div style={{ marginBottom: '15px' }}>
                        <span style={{ color: '#a0aec0', fontSize: '14px' }}>License ID:</span>
                        <div style={{ color: '#e0e0e0', fontWeight: 'bold', fontSize: '16px' }}>
                          {userLicense.id}
                        </div>
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <span style={{ color: '#a0aec0', fontSize: '14px' }}>Status:</span>
                        <div>
                          <span style={{ 
                            padding: '4px 12px', 
                            borderRadius: '12px', 
                            fontSize: '14px',
                            background: '#48bb78',
                            color: 'white',
                            fontWeight: '500'
                          }}>
                            Active
                          </span>
                        </div>
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <span style={{ color: '#a0aec0', fontSize: '14px' }}>Subscription:</span>
                        <div style={{ color: '#e0e0e0', fontWeight: 'bold' }}>
                          $50/month
                        </div>
                      </div>
                      {userLicense.expirationDate && (
                        <div>
                          <span style={{ color: '#a0aec0', fontSize: '14px' }}>Expires:</span>
                          <div style={{ color: '#e0e0e0' }}>
                            {new Date(userLicense.expirationDate).toLocaleDateString()}
                          </div>
        </div>
      )}
                    </>
                  );
                } else {
                  return (
                    <div style={{ color: '#a0aec0', textAlign: 'center', padding: '20px' }}>
                      No license assigned to your account.
                      <br />
                      Contact your administrator to get a license.
                    </div>
                  );
                }
              })()}
            </div>
          </div>

          {/* Company Information */}
          <div style={{ marginBottom: '30px' }}>
            <h4>Company Information</h4>
            <div style={{ 
              background: '#4a5568', 
              padding: '20px', 
              borderRadius: '8px',
              maxWidth: '500px'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <span style={{ color: '#a0aec0', fontSize: '14px' }}>Company Name:</span>
                <div style={{ color: '#e0e0e0', fontWeight: 'bold', fontSize: '16px' }}>
                  {companyData.name}
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <span style={{ color: '#a0aec0', fontSize: '14px' }}>Total Licenses:</span>
                <div style={{ color: '#e0e0e0', fontWeight: 'bold' }}>
                  {companyData.licenses?.length || 0}
                </div>
              </div>
              <div>
                <span style={{ color: '#a0aec0', fontSize: '14px' }}>Administrator:</span>
                <div style={{ color: '#e0e0e0' }}>
                  {(() => {
                    const admin = membersData.find(member => member.uid === companyData.adminUserId);
                    return admin ? `${admin.name} (${admin.email})` : 'Unknown';
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Team Members (Read-Only) */}
          <div style={{ marginBottom: '30px' }}>
            <h4>Team Members</h4>
            <div style={{ 
              background: '#4a5568', 
              padding: '15px', 
              borderRadius: '8px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#2d3748' }}>
                    <th style={{ padding: '12px', border: '1px solid #6b7280', textAlign: 'left', color: '#e0e0e0' }}>Name</th>
                    <th style={{ padding: '12px', border: '1px solid #6b7280', textAlign: 'left', color: '#e0e0e0' }}>Email</th>
                    <th style={{ padding: '12px', border: '1px solid #6b7280', textAlign: 'left', color: '#e0e0e0' }}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {memberRows.map((row, index) => (
                    <tr key={index} style={{ background: '#4a5568' }}>
                      <td style={{ padding: '12px', border: '1px solid #6b7280', color: '#e0e0e0' }}>
                        {row.name}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #6b7280', color: '#e0e0e0' }}>
                        {row.email}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #6b7280', color: '#e0e0e0' }}>
                        {row.isAdmin ? 'Administrator' : 'Team Member'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ 
            background: '#374151', 
            padding: '15px', 
            borderRadius: '8px', 
            border: '1px solid #6b7280',
            maxWidth: '500px'
          }}>
            <p style={{ margin: '0', color: '#a0aec0', fontSize: '14px', lineHeight: '1.5' }}>
              💡 <strong>Need help?</strong> Contact your administrator ({(() => {
                const admin = membersData.find(member => member.uid === companyData.adminUserId);
                return admin ? admin.email : 'Unknown';
              })()}) for license management, team changes, or billing questions.
            </p>
          </div>
        </div>
      )}
      
      {/* Custom Confirmation Modal */}
      {showConfirmModal && confirmModalData && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          background: 'rgba(0,0,0,0.7)', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{ 
            background: '#2d3748', 
            padding: '30px', 
            borderRadius: '12px', 
            minWidth: '400px',
            maxWidth: '600px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            border: '1px solid #4a5568'
          }}>
            {confirmModalData.type === 'cancel' ? (
              // Cancellation Modal
              <>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#e0e0e0', fontSize: '24px' }}>
                    {confirmModalData.isAdminLicense ? 'Cancel Admin License?' : 'Cancel License?'}
                  </h3>
                </div>

                {confirmModalData.isAdminLicense && confirmModalData.totalLicenses > 1 && (
                  <div style={{ 
                    background: '#e53e3e', 
                    padding: '15px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '2px solid #ff6b6b'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: 'white' }}>⚠️ WARNING: Admin License!</h4>
                    <p style={{ margin: '0 0 10px 0', color: 'white', lineHeight: '1.4' }}>
                      {confirmModalData.hasOtherAssignedLicenses 
                        ? 'Before cancelling your admin license, you must assign admin privileges to another team member, or cancel ALL licenses if closing your account.'
                        : 'You have other available licenses but no other assigned users. Consider assigning a license to another team member first, or cancel ALL licenses if closing your account.'
                      }
                    </p>
                    <p style={{ margin: '0', color: 'white', fontWeight: 'bold' }}>
                      If you cancel your admin license without transferring admin rights, your team will lose access to account management.
                    </p>
                  </div>
                )}

                <div style={{ 
                  background: '#4a5568', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  marginBottom: '25px' 
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#e0e0e0' }}>License: {confirmModalData.licenseId}</h4>
                  <p style={{ margin: '0 0 10px 0', color: '#a0aec0', lineHeight: '1.4' }}>
                    This will:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px', color: '#a0aec0', lineHeight: '1.4' }}>
                    <li>Cancel the $50/month PayPal subscription</li>
                    <li><strong>License remains active until the end of the current billing period</strong></li>
                    <li>License will be automatically removed when the billing period ends</li>
                    <li>User will lose access only after the expiration date</li>
                  </ul>
                  <p style={{ margin: '10px 0 0 0', color: '#a0aec0', fontSize: '14px' }}>
                    You will be redirected to PayPal to cancel the subscription.
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button 
                    onClick={confirmModalData.onCancel}
                    style={{ 
                      padding: '12px 24px', 
                      background: '#6b7280', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={confirmModalData.onConfirm}
                    style={{ 
                      padding: '12px 24px', 
                      background: '#e53e3e', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    {confirmModalData.isAdminLicense && confirmModalData.totalLicenses > 1 
                      ? 'Cancel Admin License Anyway' 
                      : 'Cancel License'
                    }
                  </button>
                </div>
              </>
            ) : (
              // Subscription Modal
              <>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>💳</div>
                  <h3 style={{ margin: '0 0 10px 0', color: '#e0e0e0', fontSize: '24px' }}>
                    Purchase {confirmModalData.licenseCount} License{confirmModalData.licenseCount > 1 ? 's' : ''}?
                  </h3>
                  <p style={{ margin: '0', color: '#a0aec0', fontSize: '18px' }}>
                    Total: ${confirmModalData.totalCost}/month
                  </p>
                </div>

                <div style={{ 
                  background: '#4a5568', 
                  padding: '20px', 
                  borderRadius: '8px', 
                  marginBottom: '20px' 
                }}>
                  {confirmModalData.licenseCount === 1 ? (
                    <>
                      <h4 style={{ margin: '0 0 15px 0', color: '#4A90E2' }}>Single License Subscription</h4>
                      <p style={{ margin: '0', color: '#e0e0e0', lineHeight: '1.5' }}>
                        This will create one PayPal subscription for $50/month that you can manage or cancel anytime.
                      </p>
                    </>
                  ) : (
                    <>
                      <h4 style={{ margin: '0 0 15px 0', color: '#4A90E2' }}>Multiple Individual Subscriptions</h4>
                      <div style={{ 
                        background: '#e53e3e', 
                        padding: '12px', 
                        borderRadius: '6px', 
                        marginBottom: '15px' 
                      }}>
                        <p style={{ margin: '0', color: 'white', fontWeight: 'bold' }}>
                          IMPORTANT: This will create {confirmModalData.licenseCount} separate $50/month PayPal subscriptions.
                        </p>
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <h5 style={{ margin: '0 0 8px 0', color: '#e0e0e0' }}>Why separate subscriptions?</h5>
                        <ul style={{ margin: '0', paddingLeft: '20px', color: '#a0aec0' }}>
                          <li>You can cancel individual licenses as needed</li>
                          <li>Each license can be managed independently</li>
                          <li>More flexibility for your team</li>
                        </ul>
                      </div>
                      <div style={{ 
                        background: '#ed8936', 
                        padding: '12px', 
                        borderRadius: '6px' 
                      }}>
                        <p style={{ margin: '0', color: 'white', fontSize: '14px' }}>
                          <strong>Approval Process:</strong> You'll need to approve each subscription individually through PayPal.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button 
                    onClick={confirmModalData.onCancel}
                    style={{ 
                      padding: '12px 24px', 
                      background: '#6b7280', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={confirmModalData.onConfirm}
                    style={{ 
                      padding: '12px 24px', 
                      background: '#4A90E2', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    Continue to PayPal
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountSettings; 